/**
 * /configuracion — Hub de settings del facturador.
 *
 * Cards:
 *   1. Profile (resumen + link a editar wizard)
 *   2. Cert ECD (status: mandato Taxor / BYO con expiración)
 *   3. Resolución DIAN (visible, no editable post-onboarding)
 *
 * Editar resolución requiere flow especial DIAN (nueva resolución, etc.)
 * — sprint H+ tendrá ese flow.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Edit3, Loader2, RefreshCw, ShieldAlert, ShieldCheck, Upload } from 'lucide-react'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { certApi, profileApi } from '@/lib/api'
import { ROUTES } from '@/lib/constants'
import { CertUploadModal } from './cert-upload-modal'

export function SettingsPage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const [certModalOpen, setCertModalOpen] = useState(false)

  const profile = useQuery({
    queryKey: ['settings-profile', activeOrgId],
    queryFn: () => profileApi.get(activeOrgId!),
    enabled: !!activeOrgId,
  })

  const cert = useQuery({
    queryKey: ['settings-cert', activeOrgId],
    queryFn: () => certApi.get(activeOrgId!),
    enabled: !!activeOrgId,
    retry: false,
  })

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl">
        <h1 className="text-3xl font-bold">Configuración</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Perfil, certificado, resolución DIAN.
        </p>

        {/* Profile card */}
        <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Perfil del facturador</h2>
            <Link
              to={ROUTES.SETTINGS_PROFILE}
              className="inline-flex items-center gap-1.5 text-xs text-brand-600 hover:underline"
            >
              <Edit3 className="h-3.5 w-3.5" /> Editar
            </Link>
          </header>
          {profile.isLoading && (
            <p className="mt-3 text-sm text-fg-muted flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </p>
          )}
          {profile.data && (
            <dl className="mt-4 grid gap-2 text-sm">
              <Row label="Razón social" value={profile.data.legal_name} />
              {profile.data.trade_name && (
                <Row label="Nombre comercial" value={profile.data.trade_name} />
              )}
              <Row label="NIT-DV" value={profile.data.nit_verification_digit} mono />
              <Row
                label="Dirección"
                value={`${profile.data.address}, ${profile.data.city} (${profile.data.department})`}
              />
              <Row label="CIIU" value={profile.data.ciiu} mono />
              <Row label="Régimen" value={profile.data.tax_regime} mono />
              <Row
                label="Responsabilidades"
                value={profile.data.fiscal_responsibilities.join(', ')}
                mono
              />
            </dl>
          )}
        </section>

        {/* Cert card */}
        <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
          <header className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Certificado de firma</h2>
            {cert.data?.has_byo_cert && (
              <button
                type="button"
                onClick={() => setCertModalOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-500/40 bg-brand-500/10 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-500/20"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Renovar
              </button>
            )}
            {!cert.isLoading && !cert.data?.has_byo_cert && (
              <button
                type="button"
                onClick={() => setCertModalOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-bg-app px-3 py-1.5 text-xs font-medium hover:bg-bg-elevated"
              >
                <Upload className="h-3.5 w-3.5" /> Cargar cert propio
              </button>
            )}
          </header>
          {cert.isLoading && (
            <p className="mt-3 text-sm text-fg-muted flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando...
            </p>
          )}
          {!cert.isLoading && (cert.error || !cert.data?.has_byo_cert) && (
            <div className="mt-4 rounded-md border border-success/30 bg-success-tint p-4">
              <div className="flex items-start gap-3">
                <ShieldCheck className="h-6 w-6 flex-none text-success-fg" />
                <div>
                  <p className="font-semibold text-success-fg">Mandato Taxor</p>
                  <p className="mt-1 text-sm text-fg-secondary">
                    Taxor firma con su cert ECD bajo Contrato de Mandato. Cero trámite ECD para ti.
                  </p>
                  <p className="mt-2 text-xs text-fg-muted">
                    Si quieres usar tu propio certificado (BYO), haz click en
                    "Cargar cert propio" arriba.
                  </p>
                </div>
              </div>
            </div>
          )}
          {cert.data?.has_byo_cert && cert.data.cert && (
            <div className="mt-4 space-y-3">
              <div className="rounded-md border border-info/30 bg-info/10 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-6 w-6 flex-none text-info" />
                  <div className="flex-1">
                    <p className="font-semibold">Certificado propio (BYO)</p>
                    <dl className="mt-2 grid gap-1 text-xs">
                      <Row label="Subject CN" value={cert.data.cert.subject_cn} />
                      {cert.data.cert.subject_nit && (
                        <Row label="NIT" value={cert.data.cert.subject_nit} mono />
                      )}
                      <Row label="Issuer" value={cert.data.cert.issuer_normalized} mono />
                      <Row label="Algoritmo" value={cert.data.cert.key_algorithm} mono />
                      <Row
                        label="Vigente hasta"
                        value={`${cert.data.cert.valid_to.slice(0, 10)} (${cert.data.cert.days_until_expiration} días)`}
                        mono
                      />
                    </dl>
                  </div>
                </div>
              </div>
              {cert.data.cert.days_until_expiration < 30 && (
                <div className="rounded-md border border-warning/30 bg-warning-tint p-3 text-xs text-warning-fg">
                  <ShieldAlert className="mr-1 inline h-4 w-4" />
                  Tu certificado expira en menos de 30 días. Renueva antes para no
                  interrumpir emisión.
                </div>
              )}
            </div>
          )}
        </section>

        {/* Cert upload/renew modal */}
        {certModalOpen && activeOrgId && (
          <CertUploadModal
            taxClientID={activeOrgId}
            expectedNIT={profile.data?.nit_verification_digit ? undefined : undefined}
            isRenewal={cert.data?.has_byo_cert ?? false}
            onClose={() => setCertModalOpen(false)}
          />
        )}

        {/* Resolución card con % consumido + link Editar */}
        {profile.data && (
          <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
            <header className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Resolución DIAN</h2>
              <Link
                to={ROUTES.SETTINGS_RESOLUTION}
                className="inline-flex items-center gap-1.5 text-xs text-brand-600 hover:underline"
              >
                <Edit3 className="h-3.5 w-3.5" /> Gestionar
              </Link>
            </header>
            <dl className="mt-4 grid gap-2 text-sm">
              <Row label="Número" value={profile.data.resolution_number} mono />
              <Row label="Prefijo" value={profile.data.resolution_prefix} mono />
              <Row
                label="Rango"
                value={`${profile.data.resolution_from.toLocaleString(
                  'es-CO',
                )} → ${profile.data.resolution_to.toLocaleString('es-CO')}`}
                mono
              />
              <Row
                label="Vigencia"
                value={`${profile.data.resolution_valid_from.slice(0, 10)} → ${profile.data.resolution_valid_to.slice(0, 10)}`}
                mono
              />
              <Row
                label="Clave técnica (gestionada por Taxor)"
                value={
                  profile.data.resolution_technical_key
                    ? profile.data.resolution_technical_key.slice(0, 8) + '… ✓'
                    : 'pendiente de sincronizar'
                }
                mono
              />
            </dl>
            <p className="mt-3 text-xs text-fg-muted">
              En "Gestionar" verás el % consumido en vivo, alertas de
              vencimiento, y podrás cargar una nueva resolución cuando
              la actual se agote o venza.
            </p>
          </section>
        )}
      </div>
    </AppShell>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : 'text-sm'}>{value}</dd>
    </div>
  )
}
